import useSWR from 'swr';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';

import { logger } from 'src/logger';
import { swrFetcher } from 'src/utils/swr-helpers';

export interface UserRightsResponse {
  userRights: UserRight[];
}

export interface UserRightsContext {
  userRights: UserRight[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: any;
}

/**
 * Custom hook to fetch and manage user rights from the AsxCustomerProfileService (ACPS).
 *
 * This hook retrieves user permissions and authentication status by calling the ACPS
 * getUserRights endpoint. It uses SWR for data fetching with specific configuration
 * to ensure fresh data on every page load and proper error handling.
 *
 * @see {@link https://code.amazon.com/packages/AsxCustomerProfileService/blobs/mainline/--/src/main/java/com/amazon/asxcustomerprofileservice/activity/userrights/GetUserRightsActivity.java ACPS GetUserRightsActivity}
 *
 * @returns {UserRightsContext} Object containing user rights, authentication status, loading state, and any errors
 *
 * @example
 * ```tsx
 * const { userRights, isAuthenticated, isLoading, error } = useGetUserRights();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error loading user rights</div>;
 *
 * return (
 *   <div>
 *     {isAuthenticated ? 'User is authenticated' : 'User is not authenticated'}
 *     <ul>
 *       {userRights.map(right => <li key={right}>{right}</li>)}
 *     </ul>
 *   </div>
 * );
 * ```
 */
export const useGetUserRights = (): UserRightsContext => {
  // Use useSWR with a 1 hour TTL to cache user rights and reduce API calls
  // dedupingInterval prevents refetching within the specified time window (1 hour = 3600000ms)
  const ONE_HOUR_MILLIS = 3600000;
  const { data, error, isLoading } = useSWR<UserRightsResponse>('/acps/getUserRights', swrFetcher, {
    dedupingInterval: ONE_HOUR_MILLIS,
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    errorRetryCount: 1,
    onSuccess: (fetchedData) => {
      const rights = fetchedData?.userRights || [];
      sessionStorage.setItem('userRights', rights.toString());
    },
  });

  if (error) {
    logger.error('Failed to load user rights from API.', error);
  }

  const userRights = data?.userRights || [];
  const isAuthenticated = userRights.includes(UserRight.AUTHENTICATED);

  return {
    userRights,
    isAuthenticated,
    isLoading,
    error,
  };
};
